/**
 * Espejo de `core/autorizacion.js`.
 *
 * Esto **no** es seguridad: es cortesía. Sirve para no ofrecerle a un cajero un
 * botón que la API le va a negar. Quien decide de verdad es el backend, con el
 * rol que sale del token verificado y de ningún otro sitio (SPEC-008).
 *
 * Si esta tabla y la del backend se desincronizan, el peor caso es una pantalla
 * que muestra algo de más y recibe un 403 — nunca un acceso concedido.
 */

import type { Rol } from "../api/tipos.ts";

export const PERMISO = {
  VER_PAGOS: "ver_pagos",
  VER_CARTERA: "ver_cartera",
  DECIDIR: "decidir",
  EXPORTAR: "exportar",
  CONFIGURAR: "configurar",
  /** Mirar quién tiene acceso. Gestionarlo es INVITAR. */
  VER_EQUIPO: "ver_equipo",
  INVITAR: "invitar",
  MOVER_AUTONOMIA: "mover_autonomia",
  /** Registrar abonos y facturas. Distinto de DECIDIR: ver core/autorizacion.js */
  CARGAR_DATOS: "cargar_datos",
} as const;

export type Permiso = (typeof PERMISO)[keyof typeof PERMISO];

const MATRIZ: Record<Rol, Permiso[]> = {
  dueno: Object.values(PERMISO),
  contador: [
    PERMISO.VER_PAGOS,
    PERMISO.VER_CARTERA,
    PERMISO.DECIDIR,
    PERMISO.EXPORTAR,
    PERMISO.VER_EQUIPO,
    PERMISO.CARGAR_DATOS,
  ],
  cajero: [PERMISO.VER_PAGOS, PERMISO.DECIDIR],
  lectura: [PERMISO.VER_PAGOS],
};

export const puede = (rol: Rol | undefined, permiso: Permiso): boolean =>
  !!rol && (MATRIZ[rol] ?? []).includes(permiso);

export const ROLES: { valor: Rol; nombre: string; descripcion: string }[] = [
  {
    valor: "dueno",
    nombre: "Dueño",
    descripcion: "Todo: decide, ve la cartera, invita y mueve la autonomía.",
  },
  {
    valor: "contador",
    nombre: "Contador",
    descripcion: "Decide sobre los pagos, ve la cartera y exporta. Ve el equipo, no lo cambia.",
  },
  {
    valor: "cajero",
    nombre: "Cajero",
    descripcion: "Confirma pagos para entregar el pedido. No ve cuánto debe cada cliente.",
  },
  { valor: "lectura", nombre: "Solo lectura", descripcion: "Mira los pagos y no toca nada." },
];

export const nombreRol = (rol: Rol | undefined): string =>
  ROLES.find((r) => r.valor === rol)?.nombre ?? "—";
